import Head from "next/head";
import SyntaxHighlighter from "react-syntax-highlighter";

import { serialize } from "next-mdx-remote/serialize";
import { MDXRemote } from "next-mdx-remote";

import { getAllBlogPosts } from "lib/remoteContent";

import MarkdownLayout from "components/MarkdownLayout";
import PostDetails from "components/blog/PostDetails";

import Media from "components/Media";
import Banner from "components/Banner";

import type { MDXRemoteSerializeResult } from "next-mdx-remote";
import type { Page } from "types/page";
import type { GetStaticProps, GetStaticPaths } from "next";

import type { BlogPost } from "lib/remoteContent";

type PageProps = {
  post: BlogPost;
  mdxSource: MDXRemoteSerializeResult | null;
};

type PageParams = {
  slug: string;
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = (await getAllBlogPosts()).map((post) => post.slug);

  return {
    paths: paths.map((slug: string) => ({ params: { slug } })),
    fallback: "blocking",
  };
};

function isStringCode(code: React.ReactNode, className?: string): code is string | string[] {
  return !!/language-(\w+)/.exec(className || "");
}

const code: React.FC<React.PropsWithChildren & { className?: string }> = ({ children, className }) => {
  if (isStringCode(children, className)) {
    const match = /language-(\w+)/.exec(className || "");
    if (match) {
      return (
        <SyntaxHighlighter language={match[1]} useInlineStyles={false}>
          {children}
        </SyntaxHighlighter>
      );
    }
  }
  return <code className={className}>{children}</code>;
};

// `code` renderer already includes `pre`
const pre: React.FC<React.PropsWithChildren> = ({ children }) => <>{children}</>;

export const getStaticProps: GetStaticProps<PageProps, PageParams> = async (context) => {
  if (context.params === undefined) {
    return {
      notFound: true,
    };
  }

  const { slug } = context.params;
  const post = (await getAllBlogPosts()).find((post) => post.slug === slug);

  if (post) {
    const mdxSource = post.content ? await serialize(post.content) : null;

    return {
      props: {
        post,
        mdxSource,
      },
    };
  }

  return {
    notFound: true,
  };
};

const BlogPost: Page<PageProps> = ({ post, mdxSource }: PageProps) => {
  const { title, description } = post;
  return (
    <>
      <Head>
        <title>{title}</title>
      </Head>

      <MarkdownLayout
        meta={{
          title,
          desc: description,
        }}
        details={<PostDetails {...post} />}
      >
        {mdxSource ? <MDXRemote {...mdxSource} components={{ Media, Banner, code, pre }} /> : null}
      </MarkdownLayout>
    </>
  );
};

export default BlogPost;
